import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { Colors } from '@/assets';
import { Alert, DialogFullscreen, FormLayout, Loading, ModalConfirm } from '@/components';
import { TenantForm } from '@/components/TenantForm';
import { AuthActions, AuthThunks, isAdminUser, TenantInfo, useAuth } from '@/features/auth';
import { useMessageData, useOuterClick } from '@/hooks';

export const SelectTenant = () => {
    const { user, tenant } = useAuth();

    const dispatch = useDispatch();

    const { isSuccess, message, setMessage, clearMessage } = useMessageData();
    const [loading, setLoading] = useState<boolean>(false);

    const [isOpenMenu, setIsOpenMenu] = useState<boolean>(false);
    const [isNewTenant, setIsNewTenant] = useState<boolean>(false);
    const [tenantWantDelete, setTenantWantDelete] = useState<TenantInfo | undefined>();
    const [tenantEditing, setTenantEditing] = useState<TenantInfo | undefined>();

    const ref = useOuterClick(() => setIsOpenMenu(false));

    const onToggleMenu = () => {
        setIsOpenMenu((prevState) => !prevState);
    };

    const onSelectTenant = (tenant: TenantInfo) => {
        dispatch(AuthActions.setTenant(tenant));
    };

    const onDeleteTenant = async () => {
        try {
            if (!tenantWantDelete) return;
            setTenantWantDelete(undefined);
            setLoading(true);
            await dispatch(AuthThunks.deleteTenant(tenantWantDelete.id));
        } catch (error: any) {
            setMessage({ message: error.message });
        } finally {
            setLoading(false);
        }
    };

    const onCloseTenantModal = () => {
        setIsNewTenant(false);
        setTenantEditing(undefined);
    };

    if (!user || !tenant) return null;

    return (
        <>
            <Loading loading={loading} />
            <ModalConfirm
                title={`Are you sure you want to delete ${tenantWantDelete?.name}?`}
                open={!!tenantWantDelete}
                onAgree={onDeleteTenant}
                onClose={() => setTenantWantDelete(undefined)}
            />
            <DialogFullscreen open={isNewTenant || !!tenantEditing} onClose={onCloseTenantModal}>
                <FormLayout>
                    <TenantForm notSignOut tenantEditing={tenantEditing} submitSuccess={onCloseTenantModal} />
                </FormLayout>
            </DialogFullscreen>
            <Alert isSuccess={isSuccess} message={message} clearMessage={clearMessage} />
            <Box ref={ref} sx={{ position: 'relative' }}>
                <Button
                    onClick={onToggleMenu}
                    sx={{
                        textTransform: 'none',
                        width: '100%',
                        border: '1px solid',
                        borderColor: isOpenMenu ? Colors.mauveViolet : Colors.linkWater,
                        borderRadius: '8px',
                        px: 1.8,
                        height: 50,
                        boxShadow: isOpenMenu
                            ? '0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px #F4EBFF'
                            : '0px 1px 2px rgba(16, 24, 40, 0.05)',
                        backgroundColor: Colors.white,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        outline: 'none',
                        gap: 1
                    }}
                >
                    <Typography
                        variant="h6"
                        className="line-clamp-1"
                        sx={{ fontWeight: 400, color: Colors.tangaroaBlue, textAlign: 'start' }}
                    >
                        {tenant.name}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography
                            className="far fa-chevron-up"
                            variant="subtitle1"
                            sx={{ height: 10, color: Colors.lynchBlue }}
                        ></Typography>
                        <Typography className="far fa-chevron-down" variant="subtitle1" sx={{ color: Colors.lynchBlue }}></Typography>
                    </Box>
                </Button>
                {isOpenMenu && (
                    <Box
                        sx={{
                            width: '100%',
                            position: 'absolute',
                            backgroundColor: Colors.white,
                            boxShadow: '0px 12px 16px -4px rgba(16, 24, 40, 0.1), 0px 4px 6px -2px rgba(16, 24, 40, 0.05)',
                            borderRadius: '8px',
                            zIndex: 10,
                            top: '100%',
                            mt: 0.5
                        }}
                    >
                        {user.tenants.map((item, index) => {
                            const isSelected = item.tenant.id === tenant.id;
                            return (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        background: isSelected ? Colors.magnoliaWhite : ''
                                    }}
                                    key={index}
                                >
                                    <MenuButton onClick={() => onSelectTenant(item.tenant)} title={item.tenant.name} active={isSelected} />
                                    {isAdminUser(item) && (
                                        <>
                                            <IconButton onClick={() => setTenantEditing(item.tenant)} sx={{ width: 30, height: 30, p: 0 }}>
                                                <Typography
                                                    className="far fa-pen"
                                                    variant="subtitle2"
                                                    sx={{ color: Colors.lynchBlue }}
                                                ></Typography>
                                            </IconButton>
                                            <IconButton
                                                onClick={() => setTenantWantDelete(item.tenant)}
                                                sx={{ width: 30, height: 30, p: 0 }}
                                            >
                                                <Typography
                                                    className="far fa-trash-alt"
                                                    variant="subtitle2"
                                                    sx={{ color: Colors.lynchBlue }}
                                                ></Typography>
                                            </IconButton>
                                        </>
                                    )}
                                </Box>
                            );
                        })}
                        <Divider sx={{ borderColor: Colors.greenWhite }} />
                        <MenuButton onClick={() => setIsNewTenant(true)} title="Add New Workspace" />
                    </Box>
                )}
            </Box>
        </>
    );
};

const MenuButton = ({ active, title, onClick }: { active?: boolean; title: string; onClick?: () => void }) => {
    return (
        <Button
            onClick={onClick}
            sx={{
                width: '100%',
                boxShadow: 'none',
                display: 'flex',
                borderRadius: 0,
                pl: 1.8,
                height: 44,
                justifyContent: 'space-between',
                textTransform: 'none',
                gap: 1
            }}
        >
            <Typography variant="h6" className="line-clamp-1" sx={{ fontWeight: 400, color: Colors.tangaroaBlue, textAlign: 'left' }}>
                {title}
            </Typography>
            {active && (
                <Typography className="far fa-check" variant="h6" sx={{ fontWeight: 400, color: Colors.mediumPurpleViolet }}></Typography>
            )}
        </Button>
    );
};
